/**
 * Authentication API Validators
 *
 * Validators for authentication-related API requests and responses
 *
 * @module validators/auth
 */

import { z } from 'zod';
import type { AuthTokenResponse, LoginRequest, RegisterRequest } from '../contracts/rest-api.contract';

/**
 * Validates LoginRequest input
 *
 * @param data Unknown data to validate
 * @returns Validated LoginRequest
 * @throws ZodError if validation fails
 */
export const LoginRequestValidator = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

export function validateLoginRequest(data: unknown): LoginRequest {
  return LoginRequestValidator.parse(data);
}

/**
 * Type guard for LoginRequest
 *
 * @param data Unknown data to check
 * @returns true if data is LoginRequest
 */
export function isLoginRequest(data: unknown): data is LoginRequest {
  try {
    LoginRequestValidator.parse(data);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validates RegisterRequest input
 *
 * @param data Unknown data to validate
 * @returns Validated RegisterRequest
 * @throws ZodError if validation fails
 */
export const RegisterRequestValidator = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  display_name: z.string().min(1).max(255),
});

export function validateRegisterRequest(data: unknown): RegisterRequest {
  return RegisterRequestValidator.parse(data);
}

/**
 * Type guard for RegisterRequest
 *
 * @param data Unknown data to check
 * @returns true if data is RegisterRequest
 */
export function isRegisterRequest(data: unknown): data is RegisterRequest {
  try {
    RegisterRequestValidator.parse(data);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safe parser for LoginRequest that returns validation error details
 *
 * @param data Unknown data to parse
 * @returns { success: true; data } or { success: false; error }
 */
export function safeParseLoginRequest(
  data: unknown,
): { success: true; data: LoginRequest } | { success: false; error: z.ZodError } {
  const result = LoginRequestValidator.safeParse(data);
  return result;
}

/**
 * Safe parser for RegisterRequest that returns validation error details
 *
 * @param data Unknown data to parse
 * @returns { success: true; data } or { success: false; error }
 */
export function safeParseRegisterRequest(
  data: unknown,
): { success: true; data: RegisterRequest } | { success: false; error: z.ZodError } {
  const result = RegisterRequestValidator.safeParse(data);
  return result;
}

/**
 * Type guard for AuthTokenResponse
 *
 * @param data Unknown data to check
 * @returns true if data is AuthTokenResponse
 */
export function isAuthTokenResponse(data: unknown): data is AuthTokenResponse {
  try {
    z.object({
      access_token: z.string().min(1),
      refresh_token: z.string().min(1),
      expires_in: z.number().int().positive(),
      token_type: z.literal('Bearer'),
    })
      .strict()
      .parse(data);
    return true;
  } catch {
    return false;
  }
}
